import React, { Suspense, useMemo } from "react";
import { Route as ReactRoute, Switch, useLocation } from "react-router-dom";

import { Route } from "App/router";
import { IRouteType } from "App/types";
import { copy } from "shared/assets";
import { Head, Loader } from "shared/components";
import { FourOhFour } from "shared/pages";
import UserFriendlyInfoPage from "shared/pages/UserFriendlyInfoPage/UserFriendlyInfoPage";
import { Content, MainContent } from "shared/styles";
import { IAPIState, IImageStylePreset } from "shared/types";
import {
  buildUrl,
  getImageURLWithStyle,
  isVenueDomain,
  useScrollToTopOnRouteChange,
} from "shared/utils";
import { VenueSideMenu } from "Venues/containers";
import {
  StagePageProvider,
  useVenuePageContext,
  VenuePageProvider,
} from "Venues/contexts";
import { venuePageRoutes } from "Venues/router";
import { VENUE_PATHS } from "./constants";

const VenuePageRouter = () => {
  const { venue, venueId, apiMeta } = useVenuePageContext();
  const { pathname } = useLocation();

  const title = useMemo(() => {
    const titles = [];
    if (pathname === buildUrl(VENUE_PATHS.CREATE_VENUE_STAGE, { venueId })) {
      titles.push(copy.venuePage.edit.stage.short);
    }
    if (venue) {
      titles.push(
        pathname.includes(buildUrl(VENUE_PATHS.EDIT_VENUE, { venueId }))
          ? `${copy.actions.edit} ${venue.title}`
          : venue.title
      );
    }
    return titles;
  }, [venue, pathname, venueId]);

  // scroll to top between route changes
  useScrollToTopOnRouteChange();

  if (isVenueDomain && venue?.archivedAt) {
    return <UserFriendlyInfoPage message={copy.userFriendly.archivedVenue} />;
  }

  if (!venue) {
    if (apiMeta?.status !== IAPIState.failed) {
      return <Loader />;
    }
    return <FourOhFour />;
  }

  return (
    <>
      <Head
        title={title}
        og={{
          title: venue.title,
          image: getImageURLWithStyle({
            ...venue.logo,
            style: IImageStylePreset.logo,
          }),
        }}
      />
      <VenueSideMenu />
      <Content>
        <MainContent>
          <Suspense fallback={<Loader />}>
            <Switch>
              {venuePageRoutes.map((route, index) => (
                <Route
                  key={index}
                  {...route}
                  permissions={
                    route.type === IRouteType.permission
                      ? venue.permissions
                      : undefined
                  }
                />
              ))}
              <ReactRoute component={FourOhFour} />
            </Switch>
          </Suspense>
        </MainContent>
      </Content>
    </>
  );
};

const WrappedVenuePageRouter: React.FC = () => (
  <VenuePageProvider>
    <StagePageProvider>
      <VenuePageRouter />
    </StagePageProvider>
  </VenuePageProvider>
);

export default WrappedVenuePageRouter;
